import { useMemo } from 'react';
import { useAttributionOptional } from '../contexts/AttributionContext';
import { useAuth } from '../contexts/AuthContext';
import { useRewards } from '../hooks/useRewards';
import {
  buildWalletReadModel,
  formatAtomicUsdc,
  type WalletReadModel,
} from '../lib/wallet/read-model';

export interface WalletProps {
  initialState?: 'loading' | 'ready';
}

function formatDate(timestamp: number): string {
  if (!Number.isFinite(timestamp) || timestamp <= 0) return 'Unknown date';
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function Wallet({ initialState = 'ready' }: WalletProps) {
  const { wallet, isLoading: authLoading } = useAuth();
  const rewards = useRewards({ requireAuthenticatedWallet: true });
  const attribution = useAttributionOptional();

  const model = useMemo<WalletReadModel>(
    () => buildWalletReadModel({
      walletAddress: wallet?.address,
      rewards: {
        totalPoints: rewards.totalPoints,
        tier: rewards.tier,
        categoryBreakdown: rewards.categoryBreakdown,
        transactions: rewards.transactions,
      },
      settlements: attribution?.paymentLog ?? [],
    }),
    [
      wallet?.address,
      rewards.totalPoints,
      rewards.tier,
      rewards.categoryBreakdown,
      rewards.transactions,
      attribution?.paymentLog,
    ],
  );

  if (initialState === 'loading' || authLoading || rewards.isLoading) {
    return (
      <div data-testid="wallet-screen" className="px-4 pt-8">
        <div className="space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-8 w-48 bg-neutral-100 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="wallet-screen"
      className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 pt-6 pb-24"
    >
      <header className="rounded-lg border border-neutral-200 bg-white/80 p-5 shadow-sm">
        <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Read-only wallet</p>
        <h1 className="mt-1 font-serif text-3xl font-semibold text-neutral-950">Wallet</h1>
        <div
          data-testid="wallet-identity"
          data-state={model.identity.state}
          className="mt-3 text-sm text-neutral-600"
        >
          {model.identity.state === 'authenticated' ? (
            <span>Connected identity {model.identity.shortAddress}</span>
          ) : (
            <span>Connect your wallet to see locally stored rewards and settlement records.</span>
          )}
        </div>
      </header>

      <section
        data-testid="wallet-rewards-lane"
        data-visible={model.rewards.visible ? 'true' : 'false'}
        className="rounded-lg border border-neutral-200 bg-white/75 p-5 shadow-sm"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-neutral-900">Rewards points</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Current OwnYou rewards points. These are not OwnYou tokens.
            </p>
          </div>
          <div data-testid="wallet-rewards-total" className="text-right">
            <p className="font-serif text-3xl font-semibold text-neutral-950">{model.rewards.totalPoints}</p>
            <p className="text-xs uppercase tracking-wide text-neutral-500">points · {model.rewards.tier}</p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <PeriodTile label="This week" value={model.rewards.periodTotals.currentWeek} />
          <PeriodTile label="This month" value={model.rewards.periodTotals.currentMonth} />
          <PeriodTile label="All time" value={model.rewards.periodTotals.allTime} />
        </div>
        <div data-testid="wallet-rewards-categories" className="mt-4 flex flex-wrap gap-2">
          {model.rewards.categoryRows.length === 0 ? (
            <span className="text-sm text-neutral-500">No reward categories yet.</span>
          ) : model.rewards.categoryRows.map(row => (
            <span
              key={row.category}
              className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs text-neutral-700"
            >
              {row.category}: {row.points}
            </span>
          ))}
        </div>
      </section>

      <section
        data-testid="wallet-stablecoin-settlements"
        data-visible={model.settlements.visible ? 'true' : 'false'}
        data-count={model.settlements.count}
        className="rounded-lg border border-neutral-200 bg-white/75 p-5 shadow-sm"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-neutral-900">Stablecoin settlements</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Testnet settlement records from attribution payments. OwnYou-token rewards are deferred.
            </p>
          </div>
          <div data-testid="wallet-stablecoin-total" className="text-right">
            <p className="font-serif text-3xl font-semibold text-neutral-950">
              {formatAtomicUsdc(model.settlements.totalAtomicUsdc)}
            </p>
            <p className="text-xs uppercase tracking-wide text-neutral-500">USDC · {model.settlements.count} rows</p>
          </div>
        </div>
        <div className="mt-4 divide-y divide-neutral-100">
          {model.settlements.rows.length === 0 ? (
            <p className="py-3 text-sm text-neutral-500">No stablecoin settlement rows are stored yet.</p>
          ) : model.settlements.rows.map(row => (
            <div key={row.id} data-testid="wallet-settlement-row" className="flex items-center justify-between gap-3 py-3 text-sm">
              <div>
                <p className="font-medium text-neutral-900">{row.campaignRefShort}</p>
                <p className="text-xs text-neutral-500">{row.txHashShort}</p>
                <p
                  data-testid="wallet-settlement-recipient"
                  data-metadata="recipient"
                  className="text-xs text-neutral-500"
                >
                  Recipient {row.recipientShort}
                </p>
              </div>
              <p className="font-medium text-neutral-900">{formatAtomicUsdc(row.amountAtomicUsdc)} USDC</p>
            </div>
          ))}
        </div>
      </section>

      <section
        data-testid="wallet-activity-feed"
        data-count={model.activity.length}
        className="rounded-lg border border-neutral-200 bg-white/75 p-5 shadow-sm"
      >
        <h2 className="text-base font-semibold text-neutral-900">Recent activity</h2>
        <div className="mt-3 divide-y divide-neutral-100">
          {model.activity.length === 0 ? (
            <p className="py-3 text-sm text-neutral-500">No wallet activity rows are stored yet.</p>
          ) : model.activity.map(row => (
            <div
              key={row.id}
              data-testid="wallet-activity-row"
              data-lane={row.lane}
              className="flex items-start justify-between gap-3 py-3 text-sm"
            >
              <div>
                <p className="font-medium text-neutral-900">{row.title}</p>
                <p className="text-xs text-neutral-500">{row.detail}</p>
              </div>
              <p className="shrink-0 text-xs text-neutral-500">{formatDate(row.timestamp)}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        data-testid="wallet-deferred-cards"
        className="grid gap-3 sm:grid-cols-3"
      >
        {model.deferredCards.map(card => (
          <div
            key={card.id}
            data-testid={`wallet-deferred-${card.id}`}
            className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50/80 p-4"
          >
            <p className="text-sm font-semibold text-neutral-900">{card.title}</p>
            <p className="mt-1 text-xs text-neutral-500">{card.reason}</p>
            <button
              type="button"
              disabled
              className="mt-3 rounded-md border border-neutral-200 px-3 py-1 text-xs text-neutral-400"
            >
              Deferred
            </button>
          </div>
        ))}
      </section>
    </div>
  );
}

function PeriodTile({ label, value }: { label: string; value: number }) {
  return (
    <div
      data-testid="wallet-rewards-period-total"
      data-label={label}
      className="rounded-lg border border-neutral-100 bg-neutral-50 p-3"
    >
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="mt-1 text-lg font-semibold text-neutral-900">{value}</p>
    </div>
  );
}
